/**
 * Hover cards.
 *
 * Rest the pointer on something and a panel explains it - the party roster, a
 * badge and how it is earned. It opens on a delay so sweeping across a grid
 * does not flash a dozen panels, closes on a shorter one so moving into the
 * panel itself does not dismiss it, and never steals the click.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { somethingHasFocus, watchFocus } from '@/lib/layers';

const OPEN_DELAY = 380;
const CLOSE_DELAY = 120;

type Placement = { left: number; top: number };

export function useHoverCard(render: () => ReactNode, opts?: { delay?: number; width?: number }) {
  const [shown, setShown] = useState(false);
  const [place, setPlace] = useState<Placement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const width = opts?.width ?? 300;

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const position = useCallback(() => {
    const node = anchorRef.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    // Right of the anchor, flipped left when there is no room, clamped so it
    // never runs off the bottom.
    let left = r.right + 10;
    if (left + width > window.innerWidth - 10) left = Math.max(10, r.left - width - 10);
    const top = Math.max(10, Math.min(r.top, window.innerHeight - 340));
    setPlace({ left, top });
  }, [width]);

  const open = useCallback((node: HTMLElement) => {
    clearTimers();
    anchorRef.current = node;
    openTimer.current = window.setTimeout(() => {
      if (!anchorRef.current?.isConnected) return;
      position();
      setShown(true);
    }, opts?.delay ?? OPEN_DELAY);
  }, [position, opts?.delay]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setShown(false), CLOSE_DELAY);
  }, []);

  // A card left open while the page scrolls or the window resizes is worse
  // than one that simply goes away.
  useEffect(() => {
    if (!shown) return;
    const hide = () => setShown(false);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [shown]);

  useEffect(() => () => clearTimers(), []);

  /*
   * Nothing hovers while something has focus.
   *
   * Once you have opened a card or a panel you are looking at that, and a
   * hover card that appears because the pointer happened to come to rest on
   * the way there is noise over the thing you asked for. Opening anything
   * also shuts whatever was already hovering.
   */
  useEffect(() => watchFocus((busy) => {
    if (!busy) return;
    clearTimers();
    setShown(false);
  }), []);

  const props = {
    onPointerEnter: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return;
      /* A panel is open somewhere: that is what is being looked at. */
      if (somethingHasFocus()) return;
      open(e.currentTarget as HTMLElement);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') return;
      scheduleClose();
    }
  };

  const card: ReactNode = shown && place
    ? createPortal(
        <div
          style={{ left: place.left, top: place.top, width }}
          onPointerEnter={clearTimers}
          onPointerLeave={scheduleClose}
          className="pointer-events-auto fixed z-[180] overflow-hidden rounded-[var(--radius-l)] border border-line bg-surface-2 shadow-[var(--shadow-3)] animate-[hoverin_160ms_var(--ease-out-soft)]"
        >
          {render()}
          <style>{`@keyframes hoverin { from { opacity:0; transform: translateY(5px) scale(.985) } to { opacity:1; transform:none } }`}</style>
        </div>,
        document.body
      )
    : null;

  return { props, card, close: () => setShown(false) };
}

/* ------------------------------------------------------- small tip */

/**
 * A one-line tooltip. Same timing rules as the card, far less ceremony -
 * this is for a label, not a panel.
 */
export function Tip({ text, sub, children, className, side }: {
  text: string;
  sub?: string;
  children: ReactNode;
  className?: string;
  /** 'right' pins the tip beside the anchor - for the rail, where above/below would cover other buttons. */
  side?: 'right';
}) {
  const [shown, setShown] = useState(false);
  const [place, setPlace] = useState<Placement | null>(null);
  const timer = useRef<number | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const box = useRef<HTMLDivElement>(null);

  function show(e: React.PointerEvent) {
    if (e.pointerType === 'touch') return;
    const node = e.currentTarget as HTMLElement;
    anchor.current = node;
    if (timer.current) clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!node.isConnected) return;
      setPlace(null);      // measured once it is in the document
      setShown(true);
    }, 220);
  }

  function hide() {
    if (timer.current) clearTimeout(timer.current);
    anchor.current = null;
    setShown(false);
    setPlace(null);
  }

  /*
   * Measured, then placed.
   *
   * The position used to come from a CSS transform, which the entry animation
   * also sets - so the first painted frame landed at the raw coordinate and
   * the tooltip appeared to jump into place once the transform applied. Doing
   * the arithmetic here means the very first frame is already correct.
   */
  useLayoutEffect(() => {
    if (!shown || place) return;
    const node = anchor.current;
    const tip = box.current;
    if (!node || !tip) return;
    const a = node.getBoundingClientRect();
    const t = tip.getBoundingClientRect();
    if (side === 'right') {
      setPlace({
        left: a.right + 10,
        top: Math.max(8, Math.min(a.top + a.height / 2 - t.height / 2, window.innerHeight - t.height - 8))
      });
      return;
    }
    const left = Math.max(8, Math.min(
      a.left + a.width / 2 - t.width / 2,
      window.innerWidth - t.width - 8
    ));
    // Above the anchor, flipped below when there is no room up there.
    const above = a.top - t.height - 8;
    setPlace({ left, top: above < 8 ? a.bottom + 8 : above });
  }, [shown, place, side]);

  useEffect(() => {
    if (!shown) return;
    const off = () => setShown(false);
    window.addEventListener('scroll', off, true);
    window.addEventListener('resize', off);
    return () => {
      window.removeEventListener('scroll', off, true);
      window.removeEventListener('resize', off);
    };
  }, [shown]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <>
      {/* inline-flex, not display:contents - an element with contents
          generates no box at all, so it can never receive a pointer event. */}
      <span
        className={cn('inline-flex', className)}
        onPointerEnter={show}
        onPointerLeave={hide}
      >
        {children}
      </span>
      {shown
        ? createPortal(
            <div
              ref={box}
              style={{
                left: place?.left ?? 0,
                top: place?.top ?? 0,
                // Hidden for the single frame between mounting and measuring.
                visibility: place ? 'visible' : 'hidden'
              }}
              className={cn(
                // Above everything. A tooltip explains whatever is on top, so
                // it has to outrank it - at z-450 it was rendering behind the
                // picture viewer, which sits at 500.
                'pointer-events-none fixed z-[900] max-w-[260px] rounded-[calc(8px*var(--radius-mul,1))] border border-line bg-surface-4 px-2.5 py-1.5 text-center shadow-[var(--shadow-2)]',
                place && 'animate-[tipin_130ms_var(--ease-out-soft)_backwards]'
              )}
            >
              {/* Wraps. A short label like "Save" stays on one line anyway,
                  and a long one has to fold rather than run off across the
                  page and cover whatever is beside it. */}
              <div className="text-[12px] leading-snug font-bold text-balance">{text}</div>
              {sub ? <div className="mt-0.5 text-[11.5px] leading-snug text-mute">{sub}</div> : null}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
